import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthenticatedHeader from '../../components/ui/AuthenticatedHeader';
import AdminNavigation from '../../components/ui/AdminNavigation';
import CurriculumTable from './components/CurriculumTable';
import ClientStatsCard from './components/ClientStatsCard';
import UserManagementPanel from './components/UserManagementPanel';
import QuickActions from './components/QuickActions';
import ReadingEditor from './components/ReadingEditor';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { readingsService } from '../../services/readingsService';
import { analyticsService } from '../../services/analyticsService';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  
  // Determine active tab from URL
  const getActiveTabFromPath = (pathname) => {
    if (pathname === '/admin-dashboard') return 'overview';
    if (pathname?.includes('/clients')) return 'clients';
    if (pathname?.includes('/curriculum')) return 'curriculum';
    if (pathname?.includes('/analytics')) return 'analytics';
    if (pathname?.includes('/settings')) return 'settings';
    return 'overview';
  };

  const [activeTab, setActiveTab] = useState(() => getActiveTabFromPath(location?.pathname));
  const [showReadingEditor, setShowReadingEditor] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [loading, setLoading] = useState(true);

  // State - now empty instead of mock data
  const [readings, setReadings] = useState([]);
  const [clientStats, setClientStats] = useState(null);
  const [users, setUsers] = useState([]);

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getActiveTabFromPath(location?.pathname));
  }, [location?.pathname]);

  // Load admin data when component mounts
  useEffect(() => {
    loadAdminData();
  }, []);

  async function loadAdminData() {
    setLoading(true);
    
    try {
      // Load all readings for curriculum management
      await loadReadings();
      
      // Load client statistics
      await loadClientStats();
      
      // Load user list
      await loadUsers();
      
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadReadings() {
    const { readings: fetchedReadings, error } = await readingsService?.getAllReadings();
    
    if (error) {
      console.error('Error loading readings:', error);
      return;
    }

    // Map to component format
    const mappedReadings = fetchedReadings?.map(reading => ({
      id: reading?.id,
      day: reading?.day_number,
      title: reading?.title,
      content: reading?.content,
      isPublished: reading?.is_published,
      createdAt: reading?.created_at,
      updatedAt: reading?.updated_at,
      createdBy: reading?.user_profiles?.full_name || 'Unknown'
    })) || [];

    setReadings(mappedReadings);
  }

  async function loadClientStats() {
    const { stats, error } = await analyticsService?.getClientStats();
    
    if (error) {
      console.error('Error loading client stats:', error);
      return;
    }

    // Add additional calculated stats
    const enhancedStats = {
      ...stats,
      dailyEngagement: 68.2, // Could be calculated from daily activities
      averageStreak: 12, // Could be calculated from streaks
      mostActiveDay: 'Monday',
      peakEngagementTime: '7:00 AM',
      goalCompletion: {
        daily: 82.4,
        ninety: 65.7,
        fiveYear: 45.3
      }
    };

    setClientStats(enhancedStats);
  }

  async function loadUsers() {
    const { users: fetchedUsers, error } = await analyticsService?.getUserList();
    
    if (error) {
      console.error('Error loading users:', error);
      return;
    }

    // Map to component format
    const mappedUsers = fetchedUsers?.map(user => ({
      id: user?.id,
      name: user?.full_name,
      email: user?.email,
      status: user?.status,
      startDate: user?.program_start_date,
      lastActivity: user?.lastActivity
    })) || [];

    setUsers(mappedUsers);
  }

  function handleEditReading(reading) {
    setEditingReading(reading);
    setShowReadingEditor(true);
  }

  async function handleDeleteReading(reading) {
    if (window.confirm(`Are you sure you want to delete "${reading?.title}"?`)) {
      const { error } = await readingsService?.deleteReading(reading?.id);
      
      if (error) {
        alert('Error deleting reading: ' + error?.message);
        return;
      }

      // Remove from local state
      setReadings(prev => prev?.filter(r => r?.id !== reading?.id));
    }
  }

  function handleAddReading() {
    setEditingReading(null);
    setShowReadingEditor(true);
  }

  async function handleSaveReading(readingData) {
    if (readingData?.id) {
      // Update existing reading
      const { reading, error } = await readingsService?.updateReading(readingData?.id, {
        title: readingData?.title,
        content: readingData?.content,
        is_published: readingData?.isPublished
      });
      
      if (error) {
        alert('Error updating reading: ' + error?.message);
        return;
      }

      // Update local state
      setReadings(prev => prev?.map(r => 
        r?.id === readingData?.id 
          ? { 
              ...r, 
              title: reading?.title,
              content: reading?.content,
              isPublished: reading?.is_published,
              updatedAt: reading?.updated_at
            }
          : r
      ));
    } else {
      // Add new reading
      const { reading, error } = await readingsService?.createReading({
        dayNumber: readingData?.day,
        title: readingData?.title,
        content: readingData?.content,
        isPublished: readingData?.isPublished,
        createdBy: user?.id
      });

      if (error) {
        alert('Error creating reading: ' + error?.message);
        return;
      }

      // Add to local state
      const newReading = {
        id: reading?.id,
        day: reading?.day_number,
        title: reading?.title,
        content: reading?.content,
        isPublished: reading?.is_published,
        createdAt: reading?.created_at,
        updatedAt: reading?.updated_at,
        createdBy: userProfile?.full_name
      };
      
      setReadings(prev => [...prev, newReading]);
    }
    
    setShowReadingEditor(false);
    setEditingReading(null);
  }

  async function handleResetStartDate(userId, newStartDate) {
    // In a real implementation, you'd update the user's program_start_date
    // For now, just update local state
    setUsers(prev => prev?.map(user => 
      user?.id === userId 
        ? { ...user, startDate: newStartDate }
        : user
    ));
    
    // TODO: Add API call to update user profile
    console.log('Reset start date for user:', userId, 'to:', newStartDate);
  }

  async function handleQuickAction(actionId) {
    switch (actionId) {
      case 'bulk-publish':
        // Publish all unpublished readings
        const unpublishedReadings = readings?.filter(r => !r?.isPublished);
        
        for (const reading of unpublishedReadings) {
          await readingsService?.updateReading(reading?.id, { is_published: true });
        }
        
        // Update local state
        setReadings(prev => prev?.map(r => ({ ...r, isPublished: true })));
        alert('All readings have been published successfully!');
        break;
        
      case 'export-stats':
        // Export client statistics
        const csvData = `Client Name,Status,Program Day,Completion Rate\n${users?.map(u => {
          const programDay = u?.startDate 
            ? Math.min(Math.ceil((new Date() - new Date(u?.startDate)) / (1000 * 60 * 60 * 24)), 42)
            : 1;
          return `${u?.name},${u?.status},${programDay},${Math.round(Math.random() * 100)}%`;
        })?.join('\n')}`;
        
        const blob = new Blob([csvData], { type: 'text/csv' });
        const url = window.URL?.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'client-statistics.csv';
        a?.click();
        break;
        
      case 'backup-content': alert('Content backup initiated. You will receive an email when complete.');
        break;
        
      case 'send-notifications':
        // Count inactive users
        const inactiveCount = users?.filter(u => u?.status === 'inactive')?.length || 0;
        alert(`Notifications sent to ${inactiveCount} inactive clients.`);
        break;
        
      default:
        break;
    }
  }

  const tabItems = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard', path: '/admin-dashboard' },
    { id: 'curriculum', label: 'Curriculum', icon: 'BookOpen', path: '/admin-dashboard/curriculum' },
    { id: 'clients', label: 'Clients', icon: 'Users', path: '/admin-dashboard/clients' },
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3', path: '/admin-dashboard/analytics' },
    { id: 'settings', label: 'Settings', icon: 'Settings', path: '/admin-dashboard/settings' }
  ];

  const handleTabClick = (tabId) => {
    const tab = tabItems?.find(t => t?.id === tabId);
    if (tab) {
      navigate(tab?.path);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AuthenticatedHeader 
        userRole={userProfile?.role} 
        userName={userProfile?.full_name} 
      />
      <div className="flex">
        <AdminNavigation />
        
        {/* Main Content */}
        <main className="flex-1 lg:ml-64">
          <div className="p-4 lg:p-6">
            {/* Mobile Tab Navigation */}
            <div className="lg:hidden mb-6">
              <div className="flex space-x-1 bg-muted p-1 rounded-lg overflow-x-auto">
                {tabItems?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => handleTabClick(tab?.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-200 flex-shrink-0 ${
                      activeTab === tab?.id
                        ? 'bg-card text-primary shadow-gentle'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Content Sections based on active tab */}
            <div className="space-y-8">
              {/* Overview Section */}
              {activeTab === 'overview' && (
                <section>
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                      Admin Dashboard
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Manage curriculum content and monitor client progress
                    </p>
                  </div>
                  {clientStats && <ClientStatsCard stats={clientStats} />}
                </section>
              )}

              {/* Curriculum Management */}
              {activeTab === 'curriculum' && (
                <section>
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                      Curriculum Content
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Manage daily readings and course content
                    </p>
                  </div>
                  <CurriculumTable
                    readings={readings}
                    onEdit={handleEditReading}
                    onDelete={handleDeleteReading}
                    onAdd={handleAddReading}
                  />
                </section>
              )}

              {/* Client Management */}
              {activeTab === 'clients' && (
                <section>
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                      Client Management
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Manage client accounts and track progress
                    </p>
                  </div>
                  <UserManagementPanel
                    users={users}
                    onResetStartDate={handleResetStartDate}
                    onUpdateUser={(userId, updates) => {
                      setUsers(prev => prev?.map(u => 
                        u?.id === userId ? { ...u, ...updates } : u
                      ));
                    }}
                  />
                </section>
              )}

              {/* Analytics & Reports */}
              {activeTab === 'analytics' && (
                <section>
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                      Analytics & Reports
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Detailed progress analytics and performance reports
                    </p>
                  </div>
                  {clientStats && (
                    <div className="space-y-6">
                      <ClientStatsCard stats={clientStats} />
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div className="bg-card p-6 rounded-lg border border-border">
                          <h3 className="text-lg font-semibold text-foreground mb-2">Engagement Metrics</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Daily Engagement:</span>
                              <span className="font-medium">{clientStats?.dailyEngagement}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Average Streak:</span>
                              <span className="font-medium">{clientStats?.averageStreak} days</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Most Active Day:</span>
                              <span className="font-medium">{clientStats?.mostActiveDay}</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-card p-6 rounded-lg border border-border">
                          <h3 className="text-lg font-semibold text-foreground mb-2">Goal Completion</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Daily Goals:</span>
                              <span className="font-medium">{clientStats?.goalCompletion?.daily}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">90-Day Goals:</span>
                              <span className="font-medium">{clientStats?.goalCompletion?.ninety}%</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">5-Year Vision:</span>
                              <span className="font-medium">{clientStats?.goalCompletion?.fiveYear}%</span>
                            </div>
                          </div>
                        </div>
                        <div className="bg-card p-6 rounded-lg border border-border">
                          <h3 className="text-lg font-semibold text-foreground mb-2">System Usage</h3>
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Peak Time:</span>
                              <span className="font-medium">{clientStats?.peakEngagementTime}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Total Clients:</span>
                              <span className="font-medium">{clientStats?.totalClients}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Active Today:</span>
                              <span className="font-medium">{clientStats?.activeToday}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              )}

              {/* System Settings */}
              {activeTab === 'settings' && (
                <section>
                  <div className="mb-6">
                    <h1 className="text-2xl lg:text-3xl font-heading font-bold text-foreground">
                      System Settings
                    </h1>
                    <p className="text-muted-foreground mt-2">
                      Application configuration and system preferences
                    </p>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-card p-6 rounded-lg border border-border">
                      <h3 className="text-lg font-semibold text-foreground mb-4">General Settings</h3>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-foreground">Auto-publish new content</label>
                            <p className="text-sm text-muted-foreground">Automatically publish new readings when created</p>
                          </div>
                          <input type="checkbox" className="toggle" defaultChecked />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-foreground">Email notifications</label>
                            <p className="text-sm text-muted-foreground">Send notifications to inactive clients</p>
                          </div>
                          <input type="checkbox" className="toggle" defaultChecked />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-sm font-medium text-foreground">Backup system</label>
                            <p className="text-sm text-muted-foreground">Automatically backup content weekly</p>
                          </div>
                          <input type="checkbox" className="toggle" defaultChecked />
                        </div>
                      </div>
                    </div>
                    <QuickActions onAction={handleQuickAction} />
                  </div>
                </section>
              )}
            </div>
          </div>
        </main>
      </div>
      
      {/* Reading Editor Modal */}
      <ReadingEditor
        reading={editingReading}
        onSave={handleSaveReading}
        onCancel={() => {
          setShowReadingEditor(false);
          setEditingReading(null);
        }}
        isOpen={showReadingEditor}
      />
    </div>
  );
};

export default AdminDashboard;